import { HttpClient } from './HttpClient';
import { MCPToolCreate, MCPAction, MCPActionCreate, MCPToolsListResponse, MCPToolDetailResponse, MCPToolOperationResponse, MCPActionOperationResponse, ActionTestRequest, ActionTestResponse } from '@/types/mcpTool';

/**
 * Normalize backend action response to match frontend MCPAction interface
 * Backend returns: { name, method, ... }
 * Frontend expects: { action_name, http_method, ... }
 */
const normalizeAction = (action: any): MCPAction => {


  // Smart detection: If graphql_config exists, this MUST be a GraphQL action
  const isGraphQL = !!action.graphql_config;

  const normalized = {
    action_name: action.name || action.action_name,
    endpoint: action.endpoint,
    http_method: action.method || action.http_method,
    // If graphql_config exists, force api_method to GRAPHQL
    api_method: isGraphQL
      ? 'GRAPHQL'
      : (action.api_method || action.method || action.http_method),
    id: action.id,
    param_configs: action.param_configs,
    form_config: action.form_config,
    parameters: action.parameters,
    example_response: action.example_response,
    response_projection: action.response_projection,
    graphql_config: action.graphql_config,
    request_config: action.request_config,
    created_at: action.created_at,
    updated_at: action.updated_at,
    tool_name: action.tool_name,
    workspace_id: action.workspace_id,
    created_by_user_id: action.created_by_user_id,
  };


  return normalized;
};

export const MCPToolsService = {
  /**
   * Fetch all MCP tools for the current workspace
   * Uses GET /api/tools?workspace_id={uuid}&client_id={uuid}&limit=N
   * Calls the Node server (HttpClient), not the Python MCP server.
   */
  async fetchMCPTools(params?: {
    limit?: number;
  }): Promise<MCPToolsListResponse> {
    try {
      // HttpClient.apiClient auto-injects workspace_id, client_id, and auth
      const response = await HttpClient.apiClient.get('/tools', {
        params: { limit: params?.limit ?? 100 },
      });

      // Response is wrapped: { status: "success", data: { tools: [...], count: N } }
      const data = response.data?.data ?? response.data;
      return {
        tools: data.tools || [],
        count: data.count ?? data.tools?.length ?? 0,
      };
    } catch (error) {
      console.error('Error fetching MCP tools:', error);
      throw error;
    }
  },

  /**
   * Fetch single tool with all its actions
   * Uses GET /api/tools/:toolId on the Node server
   */
  async fetchMCPToolDetail(toolId: string): Promise<MCPToolDetailResponse> {
    try {
      const response = await HttpClient.apiClient.get(`/tools/${encodeURIComponent(toolId)}`);

      // Response is wrapped: { status: "success", data: { tool: {...}, actions: [...] } }
      const data = response.data?.data ?? response.data;

      // Normalize action field names from backend format to frontend format
      if (data.actions && Array.isArray(data.actions)) {
        data.actions = data.actions.map(normalizeAction);
      }

      return data;
    } catch (error) {
      console.error(`Error fetching MCP tool ${toolId}:`, error);
      throw error;
    }
  },

  async createMCPTool(data: MCPToolCreate): Promise<MCPToolOperationResponse> {
    return (await HttpClient.apiClient.post('/tools', data)).data.data;
  },
  async updateMCPTool(toolId: string, data: MCPToolCreate): Promise<MCPToolOperationResponse> {
    return (await HttpClient.apiClient.put(`/tools/${encodeURIComponent(toolId)}`, data)).data.data;
  },
  async deleteMCPTool(toolId: string): Promise<MCPToolOperationResponse> {
    return (await HttpClient.apiClient.delete(`/tools/${encodeURIComponent(toolId)}`, {data:{}})).data.data;
  },
  async createMCPAction(toolId: string, data: MCPActionCreate): Promise<MCPActionOperationResponse> {
    return (await HttpClient.apiClient.post(`/tools/${encodeURIComponent(toolId)}/actions`, data)).data.data;
  },
  async updateMCPAction(toolId: string, actionName: string, data: MCPActionCreate): Promise<MCPActionOperationResponse> {
    return (await HttpClient.apiClient.put(`/tools/${encodeURIComponent(toolId)}/actions/${encodeURIComponent(actionName)}`, data)).data.data;
  },
  async testAction(toolId: string, action: MCPActionCreate, data: ActionTestRequest): Promise<ActionTestResponse> {
    return (await HttpClient.apiClient.post(`/tools/${encodeURIComponent(toolId)}/test`, {action, parameters:data.parameters})).data.data;
  },
  async testConnection(): Promise<boolean> {
    try { await HttpClient.apiClient.get('/tools', {params:{limit:1}}); return true; }
    catch { return false; }
  },
};
export default MCPToolsService;
